import React, { CSSProperties, useState, useEffect, useCallback } from 'react';
import { Column } from 'primereact/column';
import { DataTable, DataTablePageEvent } from 'primereact/datatable';
import { AddNewMajorDialog } from './components/add-new-major-dialog';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Major } from '../../../../../api/types/settings/studies/major';
import dateUtils from '../../../../../utils/date-format';
import { deleteMajorByIdOperation } from './operations/delete-major-by-id-operation';
import { observer } from 'mobx-react-lite';
import { EditTextDialog } from '../../../componenets/edit-entity-name-dialog/edit-text-dialog';
import { updateEntityNameOperation } from '../../../operations/update-entity-name/update-entity-name-operation';
import { SettingsEntityType } from '../../../operations/update-entity-name/settings-entity-types';
import appApi from '../../../../../api/app-api';

const ITEMS_PER_PAGE = 10;

export const SettingsEducationMajorsTabs = observer(() => {
  const [addNewMajor, setAddNewMajor] = useState(false);
  const [editMajor, setEditMajor] = useState<Major | null>(null);
  const [selectedMajors, setSelectedMajors] = useState<Major[]>([]);
  const [loading, setLoading] = useState(false);

  const cellStyle = { textAlign: 'right' } as CSSProperties;

  // Pagination state
  const [majors, setMajors] = useState<Major[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(ITEMS_PER_PAGE);

  const loadMajors = useCallback(async (offset: number, limit: number) => {
    setTableLoading(true);
    try {
      const response = await appApi.getMajorsPaginated(offset, limit);
      if (response.data.body) {
        setMajors(response.data.body.data);
        setTotalRecords(response.data.body.total);
      }
    } catch (error) {
      console.error('Error loading majors:', error);
    } finally {
      setTableLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMajors(first, rows);
  }, [first, rows, loadMajors]);

  const onPageChange = (event: DataTablePageEvent) => {
    setFirst(event.first);
    setRows(event.rows);
  };

  const refreshData = () => {
    loadMajors(first, rows);
  };

  const [deleteMajorId, setDeleteMajorId] = useState<number | null>(null);

  const handleDeleteMajor = async (majorId: number) => {
    await deleteMajorByIdOperation(majorId, setDeleteMajorId);
    refreshData();
  };

  const handleEditMajor = (major: Major) => {
    setEditMajor(major);
  };

  const handleSaveMajor = async (id: number, newName: string) => {
    await updateEntityNameOperation(
      SettingsEntityType.MAJOR,
      id,
      newName,
      setLoading,
    );
    refreshData();
  };

  const handleDialogHide = (shouldRefresh?: boolean) => {
    setAddNewMajor(false);
    if (shouldRefresh) {
      refreshData();
    }
  };

  return (
    <div>
      <Button
        onClick={() => setAddNewMajor(true)}
        label={'הוספה'}
        style={{ height: '30px', marginBottom: '8px' }}
      />
      <Dialog
        header="הוספת מגמה"
        visible={addNewMajor}
        style={{
          backgroundColor: 'white',
          padding: '8px',
          borderRadius: '8px',
        }}
        onHide={() => handleDialogHide()}
      >
        <AddNewMajorDialog onHide={() => handleDialogHide(true)} />
      </Dialog>

      <EditTextDialog
        id={editMajor?.id}
        value={editMajor?.majorName}
        title="עריכת מגמה"
        label="שם המגמה"
        loading={loading}
        onSave={handleSaveMajor}
        onHide={() => setEditMajor(null)}
      />

      <DataTable
        emptyMessage={<div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>}
        selectionMode="multiple"
        selection={selectedMajors}
        onSelectionChange={(e) => setSelectedMajors(e.value)}
        value={majors}
        loading={tableLoading}
        paginator
        lazy
        first={first}
        rows={rows}
        totalRecords={totalRecords}
        onPage={onPageChange}
        rowsPerPageOptions={[5, 10, 25, 50]}
      >
        <Column style={cellStyle} field="majorName" header="שם"></Column>
        <Column
          style={cellStyle}
          field="createdAt"
          body={(data: Major) => (
            <div>{dateUtils.displayFormatDate(data.createdAt)}</div>
          )}
          header="תאריך יצירה"
        ></Column>

        <Column
          style={cellStyle}
          field="createdByName"
          header="נוצר ע״י"
        ></Column>

        <Column
          style={{ textAlign: 'right' }}
          field="פעולות"
          header=""
          body={(data: Major) => (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button
                icon="pi pi-pencil"
                text
                severity="warning"
                tooltip={'עריכת מגמה'}
                onClick={() => handleEditMajor(data)}
              />
              <Button
                icon="pi pi-trash"
                text
                severity="danger"
                tooltip={'מחיקת מגמה'}
                loading={deleteMajorId === data.id}
                disabled={deleteMajorId !== null}
                onClick={() => handleDeleteMajor(data.id)}
              />
            </div>
          )}
        />
      </DataTable>
    </div>
  );
});
